import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { useLanguage } from '@/language/LanguageContext';

export const ProjectsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);



  const projectItems = t('projects.items');
  const projects = Array.isArray(projectItems) ? projectItems.map((item, index) => ({
    ...item,
    id: index + 1,
  })) : [];

  return (
    <section
      id="projects"
      ref={sectionRef}
      className="min-h-screen bg-card py-24 lg:py-32 px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div
          className={`mb-16 lg:mb-24 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-end justify-between">
            <div>
              <p className="text-muted-foreground text-sm font-light tracking-[0.2em] uppercase mb-4">
                {t('projects.sectionTitle')}
              </p>
              <div className="w-12 h-px bg-border" />
            </div>
          </div>
        </div>

        {/* Projects grid */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {projects.map((project, index) => (
            <article
              key={project.id}
              className={`group cursor-pointer transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${(index + 1) * 100}ms` }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <a href={project.link} target="_blank" rel="noopener noreferrer">
                {/* Image container */}
                <div className="relative overflow-hidden aspect-[4/3] mb-6">
                  <img
                    src={project.image}
                  alt={project.title}
                  className={`w-full h-full object-cover transition-transform duration-700 ${
                    hoveredIndex === index ? 'scale-105' : 'scale-100'
                  }`}
                />
                {/* Overlay */}
                <div
                  className={`absolute inset-0 bg-background/60 flex items-center justify-center transition-opacity duration-500 ${
                    hoveredIndex === index ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <div className="flex items-center gap-2 text-foreground">
                    <span className="text-sm font-light tracking-wide">
                      {t('projects.viewProject')}
                    </span>
                    <ArrowUpRight size={18} />
                  </div>
                </div>
              </div>

              {/* Project info */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-foreground text-xl lg:text-2xl font-light tracking-tight mb-2 group-hover:text-muted-foreground transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-muted-foreground text-sm font-light">
                    {project.description}
                  </p>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <p className="text-muted-foreground text-xs font-light tracking-wide">
                    {project.category}
                  </p>
                  <p className="text-muted-foreground/50 text-xs font-light mt-1">
                    {project.year}
                  </p>
                </div>
              </div>
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
